import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderWidth,
  borderStyle,
  borderRadius as radius,
} from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import {
  KG_REPORT_NODE_HEIGHT_PX,
  KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX,
  KG_REPORT_NODE_WIDTH_PX,
} from "../../kg/kgNodeDimensions";
import { kgCategorySelectionShadow } from "../../kg/kgLineageTokens";
import {
  kgNodeEllipsisTextCenterStyles,
  kgNodeEllipsisTextStyles,
  kgNodeTextContainerStyles,
} from "../../kg/kgNodeText.styles";
import type { KgNodeVisualState } from "../../kg/kgNodeShared.types";

export const Card = styled.div<{
  $visualState: KgNodeVisualState;
  $isSelected: boolean;
  $hasFooter: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${KG_REPORT_NODE_WIDTH_PX}px;
  max-width: ${KG_REPORT_NODE_WIDTH_PX}px;
  min-height: ${({ $hasFooter }) =>
    $hasFooter
      ? KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX
      : KG_REPORT_NODE_HEIGHT_PX}px;
  box-sizing: border-box;
  border-radius: ${radius.lg};
  border: ${borderWidth[1]} ${({ $visualState }) =>
    $visualState === "draft" ? borderStyle.dashed : borderStyle.solid}
    ${rawColors.accent.pink};
  background: ${rawColors.structural.white};
  box-shadow: ${({ $isSelected }) =>
    kgCategorySelectionShadow("report", $isSelected)};
  outline: none;
  transition: box-shadow 0.25s ease;
  overflow: hidden;
  cursor: pointer;
`;

/** Pink triangle fold at top-right corner. */
export const CornerFold = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: ${rawColors.accent.pink};
  clip-path: polygon(0 0, 100% 0, 100% 100%);
  border-top-right-radius: ${radius.lg};
  pointer-events: none;
`;

export const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  ${kgNodeTextContainerStyles}
`;

export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${radius.lg};
  background: ${rawColors.accent.pinkLight};
  color: ${rawColors.accent.pink};
  flex-shrink: 0;
`;

export const ReportName = styled.p`
  margin: 0;
  width: 100%;
  display: block;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  ${kgNodeEllipsisTextCenterStyles}
`;

export const Footer = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${rawColors.accent.pinkLight};
  border-top: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.accent.pink};
  ${kgNodeTextContainerStyles}
`;

export const FooterContent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  max-width: 100%;
  min-width: 0;
`;

export const BiName = styled.span`
  ${typographyMixin("overline", 4)}
  color: ${rawColors.text.mediumEmphasis};
  ${kgNodeEllipsisTextStyles}
  flex: 0 1 auto;
  min-width: 0;
  text-align: center;
`;
